// ============================================================
// TERROR TCG — RULE GATE
// ============================================================
// THE SINGLE CHOKEPOINT.
//
// ⭐ 2026-08-25, THE COHERENCY PASS. Two things this banner used to
// claim and the file did not do:
//
//   1. IT FAILED OPEN. A malformed or thrown enforcer verdict returned
//      {valid:true}. Any bug anywhere in RulesEnforcer silently became
//      "there are no rules", and the gate reported a clean pass while
//      enforcing nothing. It now fails CLOSED and says who broke.
//   2. "EVERY rule-bearing action" was not true. DRAW, DISCARD, the
//      Khao till and the Primera stamps had no wrapper — which is
//      exactly the set of rules (R07-R15, R78-R84, R88) that went
//      unenforced. They have wrappers now.
//
// Every rule-bearing action in Shatterfield — player OR NPC — passes
// through enforce() before it mutates state. There is exactly one place
// the rules are checked, and it is here. Handlers no longer decide
// legality themselves; they ask the gate, then execute only on a pass.
//
// R-UNIVERSAL: the gate does not know or care who is acting. The NPC and
// the player run the identical check. The Shatterfield plays the same way
// everywhere — free play, dungeon, story, PvP — because they all funnel
// through this one function against the one rules.js truth.
//
// Usage in a handler:
//
//   const verdict = enforce("ASCEND", { me, opponent, ... }, {
//     fromCard, toCard, isNatural, ascensionsThisTurn,
//   });
//   if (!verdict.valid) { showToast(verdict.reason, SD.red); return; }
//   // ... proceed with the mutation
//
// verdict = { valid: bool, violations: [code...], reason: string }
// ============================================================

import { validateAction } from "./RulesEnforcer";
import {
  normalizeGameState, cardById, slotHasUhlti, slotHasAffinity,
} from "./ruleAdapter";
// R108 — the phase machine. The gate does not own it; rules.js does.
import { PHASE, phaseOf, phaseAllows, canEnterPhase } from "./rules";

// The effective-Lyf resolver is injected by Shatterfield so the gate uses
// the EXACT same combat math as the board. Set once on mount.
let _effLyf = null;
export const setEffectiveLyfResolver = (fn) => { _effLyf = fn; };

/**
 * Master gate. Normalizes live Shatterfield state, then validates the
 * action against RulesEnforcer. Returns the enforcer verdict verbatim.
 *
 * @param {string} type   action type ("ATTACK", "ASCEND", "ATTACK_ANIMA"…)
 * @param {object} ctx    { me, opponent, turn, ascensionsThisTurn,
 *                          defendersAttackedThisTurn }
 * @param {object} fields action-specific payload (see RulesEnforcer.validateAction)
 */
export function enforce(type, ctx = {}, fields = {}) {
  const gameState = normalizeGameState(
    ctx.me,
    ctx.opponent,
    {
      turn: ctx.turn,
      // R08's opening Cycle is `seatCount` half-turns long, so the gate
      // has to carry the table size through. Absent = 2 = a classic
      // duel, which is what every pre-2026-08-25 caller means.
      seatCount: ctx.seatCount,
      ascensionsThisTurn: ctx.ascensionsThisTurn,
      defendersAttackedThisTurn: ctx.defendersAttackedThisTurn,
    },
    _effLyf
  );

  const action = { type, ...fields };

  // ── FAIL CLOSED ─────────────────────────────────────────────
  // A gate that returns "legal" when it could not decide is not a
  // gate. If the enforcer throws, or hands back something that is not
  // a verdict, the action is REFUSED and the diagnostic names the
  // action type and the enforcer as the culprit — so the failure is
  // attributable in a toast, a log and a harness report at once.
  let verdict;
  try {
    verdict = validateAction(action, gameState);
  } catch (err) {
    const reason = `RULE GATE FAILED CLOSED · ${type} · RulesEnforcer threw: ${err && err.message ? err.message : String(err)}`;
    // eslint-disable-next-line no-console
    console.error("[ruleGate]", reason, err);
    return { valid: false, violations: ["GATE_THREW"], reason, gateError: true };
  }

  if (!verdict || typeof verdict.valid !== "boolean") {
    const reason = `RULE GATE FAILED CLOSED · ${type} · RulesEnforcer returned a malformed verdict: ${JSON.stringify(verdict)}`;
    // eslint-disable-next-line no-console
    console.error("[ruleGate]", reason);
    return { valid: false, violations: ["GATE_MALFORMED"], reason, gateError: true };
  }

  return verdict;
}

// ── CONVENIENCE WRAPPERS ─────────────────────────────────────
// Thin helpers so handlers read cleanly. Each builds the action payload
// from live slot data and delegates to enforce().

export const gateTranscend = (ctx, esclingCard, l1Card) =>
  enforce("TRANSCEND", ctx, { escling: esclingCard, escendantL1: l1Card });

export const gateAscend = (ctx, fromCard, toCard, isNatural, ascensionsThisTurn) =>
  enforce("ASCEND", ctx, {
    fromLevel: fromCard?.level,
    toLevel: toCard?.level,
    fromCard, toCard, isNatural, ascensionsThisTurn,
  });

export const gateAttack = (ctx, attackerSlot, defenderSlot, attackerIdx, defenderIdx, lytDamage, defendersAttackedThisTurn) =>
  enforce("ATTACK", { ...ctx, defendersAttackedThisTurn }, {
    attacker: attackerSlot,
    defender: defenderSlot,
    attackerSlotIndex: attackerIdx,
    defenderSlotIndex: defenderIdx,
    lytDamage,
  });

export const gateAnimaAttack = (ctx, hasDirectAnima) =>
  enforce("ATTACK_ANIMA", ctx, { targetAnima: 0, hasDirectAnima });

// ── R08 · THE BROAD ATTACK, THE SEARCH, THE MULLIGAN (2026-08-25) ──
// caP'n's R08 ruling made "Attack" the broad word — a Tome, an Impera,
// a U'hlti, a Curio or a stamp that dims an adversary IS an Attack —
// and added two clauses nothing in the engine had ever checked: neither
// Book may be SEARCHED on the opening Cycle, and the MULLIGAN belongs
// to that Cycle and to a seat that has not yet drawn.
//
// ⚠ gateAttackEffect IS R08 AND ONLY R08. It deliberately validates
// nothing else, so it can be put in front of any damaging effect
// without that call site having to know which of the other ninety-odd
// rules also apply to it. Outside the opening Cycle it always passes,
// which is what makes it safe to sprinkle.
//
// ════════════════════════════════════════════════════════════════════
// ⚠⚠ THIS DOOR IS NOT CONNECTED, AND SAYING SO IS THE POINT.
// ════════════════════════════════════════════════════════════════════
// Measured 2026-09-14: `gateAttackEffect` has ZERO call sites in the
// whole project, and `enforce("ATTACK_EFFECT", …)` is dispatched by
// nothing. It is IMPORTED into Shatterfield.js — which is worse than not
// being imported, because the import reads as wiring. Neither
// Shatterfield.js nor tomeResolver.js asks `isOpeningTurnIndex` even
// once; only the brain does, in its own three runners.
//
// SO WHERE IS R08's BROAD CLAUSE ACTUALLY ENFORCED FOR A HUMAN? In
// exactly one place, and it is not a gate:
//
//     gameManager._r08Refusal  — reached from gameManager.updatePlayer
//     and gameManager.updateMatch, the single Firebase touchpoint. It
//     takes a FRESH read, asks rules.adversaryDamageInPayload, and
//     REFUSES THE WRITE with a console error.
//
// gameManager's own banner over that function calls it *"A REFUSAL IS
// LOUD, AND IT IS A BACKSTOP, NOT THE RULE."* A backstop that is the
// only enforcement is not a backstop. And the failure is not silent
// to the player: the fare and the card's own movement are a write to
// the ACTOR's seat, which lands, while the damage is a write to the
// ADVERSARY's seat, which is refused — so an opening-Cycle Tome flip
// costs its Khao, ruins to the Necropolis, and does nothing.
//
// ⚠ WIRING IT IS A ONE-LINE CHANGE IN TWO FILES THIS FILE DOES NOT OWN,
// so it is a handoff, not a deletion. The wrapper STAYS because the
// handoff needs it and re-writing it would be the same code again — but
// it is declared below in GATE_REACH.unreached, and
// tools/gateReach.selftest.mjs FAILS the day it gains a caller, so
// whoever wires it must delete that row on purpose and replace it with
// a behavioural assertion. That is the difference between a declared
// gap and a forgotten one.
export const gateAttackEffect = (ctx, attackType = "Effect") =>
  enforce("ATTACK_EFFECT", ctx, { attackType });

// ── THE TWO SEARCHES (caP'n, 2026-08-26) ─────────────────────
// *"brilliant distinction yes, cards are exempt from the first turn
// search rule"*.
//
// R08 bars the PLAYER'S OWN search action on the opening Cycle — the
// move that pays an R88 fare, spends the Turn's draw and digs through a
// Book. A CARD EFFECT that searches is exempt and resolves normally.
// The two are indistinguishable from the payload (both take a card out
// of a Book and put it in a hand) and perfectly distinguishable at the
// call site, which is why the call site declares it and this gate never
// guesses.
//
// TWO WRAPPERS, ON PURPOSE, rather than one with a boolean nobody
// reads. `gateSearch` is the fare-paying action and stays barred.
// `gateEffectSearch` is a card doing its printed job and passes — and
// it is greppable, so "which effects claim the exemption" is a search,
// not an audit of every `true` in the file.
//
// ⚠ AND THIS IS THE SEARCH BAN ONLY. A stamp or Tome that ALSO damages
// the adversary is still refused on the opening Cycle by the ATTACK
// ban — gateAttackEffect above — which this exemption does not touch.
// The search ban and the Attack ban are different bans and a card can
// be caught by one and not the other.
export const gateSearch = (ctx, source = "GRIMOIRE", found = null) =>
  enforce("SEARCH", ctx, { source, effectSearch: false, found });

/**
 * A CARD EFFECT that searches a Book. R08-exempt on the opening Cycle.
 * Put this in front of a tutor only when the searcher is the CARD; if
 * the seat is choosing to search and paying for it, it is gateSearch.
 */
export const gateEffectSearch = (ctx, source = "GRIMOIRE", found = null) =>
  enforce("SEARCH", ctx, { source, effectSearch: true, found });

export const gateMulligan = (ctx, { mulligansUsed = 0, drawsThisTurn = 0, handSize = null } = {}) =>
  enforce("MULLIGAN", ctx, { mulligansUsed, drawsThisTurn, handSize });

export const gateTomePlacement = (ctx) =>
  enforce("PLACE_TOME", ctx, {});

// `faceDown` is the R35 question (caP'n, 2026-09-13: the wait is a price
// on SETTING). Passed explicitly by the hand-play path, which is holding
// a card rather than a rift Tome; omitted, the validator reads it off
// the Tome and falls back to the strict answer.
export const gateTomeActivation = (ctx, tome, turnsOnField, faceDown) =>
  enforce("ACTIVATE_TOME", ctx, { tome, turnsOnField, faceDown });

// ── R108 · THE PHASE GATE — THE THREE WRAPPERS (2026-08-26) ──
// caP'n canonized five phases and one hard door between two of them:
// *"now you declare attack phase, you cannot play any more cards."*
//
// ⚠ THE ENFORCER ALREADY ASKS THE PHASE OF EVERY ACTION — see the R108
// block at the top of validateAction — so these wrappers are NOT the
// enforcement. They exist for the three things the enforcer cannot do
// from inside a validator: name the two action types that had no
// wrapper at all (ACTIVATE_SOUL, ACTIVATE_CURIO), let a UI ask "may I
// still do this?" without building an action, and validate the
// DECLARATION itself.

/**
 * A card ALREADY ATTACHED to a being's Soul — its Impera, its U'hlti,
 * its Affinity. caP'n named this as the thing that survives the
 * declaration: *"yes you can use cards attached to a being's Soul
 * here"*. It is not a card being PLAYED; it is already on the board.
 */
export const gateSoulActivation = (ctx, beingSlot, soulCardId) =>
  enforce("ACTIVATE_SOUL", ctx, { being: beingSlot, soulCard: soulCardId });

/** A Curio out of its rift. A card being played — barred in Attack. */
export const gateCurioActivation = (ctx, curioId) =>
  enforce("ACTIVATE_CURIO", ctx, { curio: curioId });

/**
 * DECLARE THE ATTACK PHASE. One-way, inside your own Turn.
 *
 * The refusals are the two ways a phase machine can be driven wrong,
 * and they are separated on purpose: going BACKWARDS is a rule
 * violation (R108's one-way clause) and re-declaring the phase you are
 * already in is a no-op the UI should swallow, not a rule break.
 */
export const gateDeclarePhase = (ctx, to = PHASE.ATTACK) => {
  const from = phaseOf(ctx?.me);
  if (from === to) return { valid: true, violations: [], reason: null, noop: true };
  if (!canEnterPhase(from, to)) {
    return {
      valid: false, violations: ["R108"],
      reason: `R108 — phases only run forwards. You are in the ${from} phase and cannot go back to ${to}.`,
    };
  }
  return { valid: true, violations: [], reason: null };
};

/**
 * "May I still do this?" — for a UI that wants to grey a button rather
 * than let it be pressed and refused. Synchronous, no state normalized,
 * reads the same rules.js predicate the enforcer reads.
 */
export const phaseGate = (ctx, actionType) => phaseAllows(actionType, phaseOf(ctx?.me));

export const gateGraveshift = (ctx) =>
  enforce("GRAVESHIFT", ctx, {});

export const gateResurrect = (ctx, fromLevel, toLevel, resurrectionsThisSolrend, cardName) =>
  enforce("RESURRECT", ctx, { fromLevel, toLevel, resurrectionsThisSolrend, cardName });

export const gateAttach = (ctx, beingSlot, attachmentId) =>
  enforce("ATTACH", ctx, { being: beingSlot, attachment: attachmentId });

export const gateAttachAffinity = (ctx, beingSlot) =>
  enforce("ATTACH_AFFINITY", ctx, { being: beingSlot });

export const gateHereticalTraining = (ctx, beingSlot) =>
  enforce("HERETICAL_TRAINING", ctx, { being: beingSlot });

// ── THE MISSING WRAPPERS (2026-08-25) ────────────────────────
// R07/R09/R11 · R10 · R12-R15 · R78-R84. Every one of these rules was
// in the catalogue, implemented in RulesEnforcer, and unreachable from
// a handler because no wrapper existed and validateAction had no case.
// Both halves are wired now.

/**
 * DRAW — R07 (first player's Turn-1 Grimoire draw is barred, the
 * LUXMOIRE draw is NOT), R11 (one draw per Turn, one lane).
 * `source` is the discriminator R07 requires: "GRIMOIRE" | "LUXMOIRE".
 */
export const gateDraw = (ctx, source = "GRIMOIRE", isFirstPlayer = false, drawsThisTurn = 0) =>
  enforce("DRAW", ctx, { source, isFirstPlayer, drawsThisTurn });

/** DISCARD / hand cap — R10, draw-then-discard at HAND_LIMIT. */
export const gateDiscard = (ctx, handAfter) =>
  enforce("DISCARD", ctx, { hand: handAfter });

/**
 * KHAO_SPEND — R15 (the cost is paid before the card acts), R13 (the
 * ceiling), R12/R14 (the refill ledger), R88 (the search fare) and
 * R96 on the Anima leg via the enforcer.
 */
export const gateKhaoSpend = (ctx, amount, spentThisTurn = false) =>
  enforce("KHAO_SPEND", ctx, { amount, spentThisTurn });

/** PRIMERA — R78-R84. The stamps had a validator and no way in. */
export const gatePrimera = (ctx, word, axis, level, opts = {}) =>
  enforce("PRIMERA", ctx, {
    word, axis, level,
    wordUsedThisTurn: !!opts.wordUsedThisTurn,
    beingUsedThisTurn: !!opts.beingUsedThisTurn,
    isOwnTurn: opts.isOwnTurn !== false,
    inResponseWindow: !!opts.inResponseWindow,
  });

// ── R32 · THE ANIMA SEAL (2026-08-25) ────────────────────────
// THE SHAPE OF THE PROBLEM. R32's validator (RulesEnforcer ·
// validateAnimaGain) was written, exported, documented as "the one
// place that can be asked", and then called by NOBODY — while 28
// hand-written `Math.min(MAX_ANIMA, x + n)` clamps did the work at the
// gain sites. That is not a rule. It is 28 authors each remembering
// the same arithmetic, and the 29th forgetting.
//
// So the seal gets a gate wrapper like every other rule (gateAnimaGain),
// AND a call shape the 28 sites can actually adopt without a
// refactor — `sealAnimaGain(current, amount)` returns THE NUMBER TO
// WRITE, which is exactly what `Math.min(...)` returned. One-for-one
// substitution, no call site restructured, and the rule is now asked
// 28 times a duel instead of zero.
//
// ⚠ R32 CAPS, IT DOES NOT FORBID, and this wrapper must never be read
// as a veto. A clamped gain is a LEGAL gain — a seat on 48 drinking 5
// lands on 50 and keeps the 3 it could not hold. `sealAnimaGain`
// therefore returns the clamped value on a pass, clamped or not.
// The one genuine refusal is a negative "gain" (a dim under a false
// name — R33's business, not R32's) and a refusal returns the seat's
// CURRENT Anima: fail closed, no mutation, and a console line naming
// the site.
//
// THE COUNTERS ARE FOR THE HARNESS. tools/balance.mjs reports them, so
// "the seal is wired" is a measured number in a batch report rather
// than a claim in a comment — the same discipline the transition
// referee's can-fail proof applies to itself. A batch that ends with
// `asked: 0` has an unwired seal again and says so out loud.
export const __animaSeal = { asked: 0, clamped: 0, spilled: 0, refused: 0 };
export const resetAnimaSealStats = () => {
  __animaSeal.asked = 0; __animaSeal.clamped = 0;
  __animaSeal.spilled = 0; __animaSeal.refused = 0;
};

export const gateAnimaGain = (ctx, amount) =>
  enforce("GAIN_ANIMA", ctx, { amount });

export const sealAnimaGain = (currentAnima, amount, site = "") => {
  const have = Number(currentAnima) || 0;
  const verdict = enforce("GAIN_ANIMA", { me: { anima: have } }, { amount });
  __animaSeal.asked++;
  if (!verdict.valid) {
    __animaSeal.refused++;
    // eslint-disable-next-line no-console
    console.error(`[ruleGate] R32 refused an Anima gain${site ? ` at ${site}` : ""}: ${verdict.reason}`);
    return have;
  }
  if (verdict.clamped) { __animaSeal.clamped++; __animaSeal.spilled += verdict.spilled || 0; }
  return verdict.value;
};

// ══════════════════════════════════════════════════════════════
// 🜂 THE EXILE WAVE'S THREE GATES (R111, R113, R115-R119)
// ══════════════════════════════════════════════════════════════
// ⚠ THESE DID NOT EXIST UNTIL 2026-09-13, AND THAT IS THE WHOLE STORY
// OF THE WAVE. `validateExiliation`, `validateExileRetrieval` and
// `validateRoleArt` were written the day the rules landed, wired into
// `validateAction`'s switch, listed in the Validators table — and had
// NO WRAPPER AND NO CALLER. A rule with a validator and no gate is a
// rule with no enforcement, because `enforce` is what carries R108's
// phase door and the fail-closed wrapper on top of it.
//
// caP'n, asked whether the wave was meant to be playable at release:
// **"1) yes"**. `src/roleArts.js` is what performs them; these are the
// three doors it goes through.

/**
 * R115–R119 — perform the Art of a rift. The rift owns the Art; the
 * Escendant standing there wears it.
 */
export const gateRoleArt = (ctx, riftIndex, rifts, opts = {}) =>
  enforce("ROLE_ART", ctx, {
    riftIndex, rifts,
    level: opts.level || 1,
    lastUsedTurn: opts.lastUsedTurn ?? null,
    wardenArtsThisTurn: opts.wardenArtsThisTurn ?? 0,
  });

/** R111 — an Exiliation. Four options, one taken, OP2. */
export const gateExiliation = (ctx, option, exiledLevel, opts = {}) =>
  enforce("EXILIATION", ctx, {
    option, exiledLevel,
    lastUsedTurn: opts.lastUsedTurn ?? null,
  });

/** R113 — the Ritual proper: buy one card back out of the Exile rift. */
export const gateExileRetrieval = (ctx, cardId, opts = {}) =>
  enforce("EXILE_RETRIEVE", ctx, {
    cardId,
    exileRift: opts.exileRift,
    lastUsedTurn: opts.lastUsedTurn ?? null,
    // ⚠ THE MARK IS PASSED ON THE ACTION, not left to be found on the
    // seat. `normalizePlayer` now carries it too — belt and braces —
    // but a rule whose entire content is "once ever" must not depend on
    // an adapter remembering to copy a field.
    exileReturned: opts.exileReturned,
    isEscendant: opts.isEscendant ?? null,
  });

// ════════════════════════════════════════════════════════════════════
// 🕰 THE THREE CLOCKS — AND WHY NONE OF THEM IS ASKED HERE (R87/R93/R126)
// ════════════════════════════════════════════════════════════════════
// RULEZ_V2 ch.19 is called "Victory & The Three Clocks" and names all
// three. A reader arriving at a file called "the single chokepoint" is
// entitled to know why it asks about none of them, so:
//
//   R87 · TWENTY MINUTES of live play. Ends the SOLREND. Asked by a
//         React effect in Shatterfield.js and closed through
//         turnEngine.endOnClock.
//   R93 · 150 HALF-TURNS (500 in live PvP). Ends the SOLREND. Asked by
//         turnEngine.handOffHalfTurn at every half-turn boundary, in
//         both drivers, and closed through the same turnEngine.endOnClock
//         so the two bells cannot end a duel differently.
//   R126 · FORTY-FIVE SECONDS a half-turn. ⚠ ENDS A TURN AND NEVER A
//         SOLREND. RULEZ_V2 ch.19: *"The third clock takes a Turn, not a
//         duel … When it reaches zero your Turn ends where it stands,
//         and whatever you had not done, you do not do."* Enforced by
//         Shatterfield's turn-timer effect, which calls its own
//         doEndTurn — i.e. turnEngine.handOffHalfTurn — and by nothing
//         else. It HOLDS while a response window is open and through
//         every gap in a chain, and it does not run at all on a Turn a
//         brain is taking.
//
// ⚠ A CLOCK IS NOT AN ACTION, WHICH IS WHY NO GATE ASKS ABOUT ONE.
// `enforce` answers "may this seat take this action, on this board" and
// a clock answers "is this Turn or this duel over" — a boundary, not a
// legality. Putting R126 behind this gate would mean every wedge in the
// game consulting a wall clock to decide whether a button is lit, and
// the phase gate (me.phase) is the seat-side latch that already carries
// "what may I still do" across a boundary. The clocks belong to
// turnEngine, which owns boundaries; this file owns actions.
//
// The one place the two touch is deliberate and capped:
// strikeLanguage.holdActive() can hold R126 for up to HOLD_CAP_MS
// (8.5s) a Turn so an animation can never cost a player a Turn, and the
// cap sits under the disconnect grace so it can never cause a rules
// event either.

// ════════════════════════════════════════════════════════════════════
// 🚪 GATE_REACH — WHICH OF THESE WRAPPERS ANYBODY CALLS
// ════════════════════════════════════════════════════════════════════
// This file's own header has twice claimed that "EVERY rule-bearing
// action passes through enforce()" and twice been wrong about it, and
// both times the evidence was a wrapper with no caller: `gateSoulActivation`
// sat exported and uncalled for weeks, and `gateAttackEffect` is still
// uncalled today. A wrapper with no caller READS AS COVERAGE, which is
// the most expensive kind of wrong in this repository.
//
// So the reach is DECLARED, not remembered. Fourteen of the twenty-eight
// wrappers have no caller. THIRTEEN OF THOSE THIRTEEN ARE HARMLESS and
// the reason is the same in every case: the handler calls
// `enforce("TYPE", gateCtx(), …)` DIRECTLY instead of through the sugar,
// so the RULE is enforced and only the convenience wrapper is unused.
// ONE is a real hole and it is named as one.
//
// ⚠ tools/gateReach.selftest.mjs ENFORCES THIS TABLE IN BOTH
// DIRECTIONS. A wrapper listed here that gains a caller fails the suite
// (delete the row, assert the refusal instead). A wrapper NOT listed
// here that loses its last caller fails the suite. The table cannot
// drift from the code without something going red.
export const GATE_REACH = {
  // Every wrapper with a live call site, for the suite to hold from the
  // other side. Kept as a list rather than derived so that losing a
  // caller is a RED TEST rather than a quietly shorter array.
  live: [
    "gateDeclarePhase", "gateDiscard", "gateDraw", "gateEffectSearch",
    "gateExileRetrieval", "gateExiliation", "gateKhaoSpend", "gateMulligan",
    "gateRoleArt", "gateSearch", "gateSoulActivation", "gateTomeActivation",
    "gateTomePlacement", "sealAnimaGain",
  ],
  unreached: {
    // ── THE ONE REAL HOLE (F1, 2026-09-14) ──────────────────────
    gateAttackEffect:
      "R08's BROAD Attack clause — a Tome, Impera, U'hlti, Curio or stamp that dims an adversary on the opening Cycle. ⚠ NOTHING ASKS IT. Shatterfield.js IMPORTS this wrapper and never calls it, and no file dispatches ATTACK_EFFECT. The only live enforcement for a human is gameManager._r08Refusal, reached from gameManager.updatePlayer and gameManager.updateMatch — which that module's own banner calls a backstop and not the rule. HANDED OFF: the damaging-effect branches of tomeResolver.applyTomeEffect and Shatterfield's soul-card dispatcher need this in front of them. See the full banner above the wrapper.",

    // ── THE THIRTEEN THAT ARE SUGAR, NOT ENFORCEMENT ────────────
    // Each of these names a rule that IS enforced — the handler simply
    // calls enforce() with the type string instead of the wrapper.
    gateTranscend:
      "R16/R17 — enforced. Shatterfield's handleTranscend calls enforce(\"TRANSCEND\", gateCtx(), …) directly; the wrapper is unused sugar.",
    gateAscend:
      "R18-R21/R102 — enforced. Shatterfield's handleAscend calls enforce(\"ASCEND\", gateCtx(), …) directly.",
    gateAttack:
      "R22-R31/R119/R121 — enforced. Shatterfield's attack resolution calls enforce(\"ATTACK\", gateCtx(), …) directly, passing both battle lines.",
    gateAnimaAttack:
      "R31/R32/R33 — enforced. Shatterfield's Anima strike calls enforce(\"ATTACK_ANIMA\", gateCtx(), { targetAnima: 0, hasDirectAnima }) directly.",
    gateGraveshift:
      "R40-R43 — enforced. Shatterfield's handleGraveshift calls enforce(\"GRAVESHIFT\", gateCtx(), {}) directly.",
    gateResurrect:
      "R44-R48/R85/R86/R89 — enforced. Both of Shatterfield's Resurrection paths call enforce(\"RESURRECT\", gateCtx(), …) directly.",
    gateAttach:
      "R70-R73 — enforced. Shatterfield's handleTrainCard and handleAttachSoulCard both call enforce(\"ATTACH\", gateCtx(), …) directly.",
    gateAttachAffinity:
      "R70/R73 — enforced. Shatterfield's handleAttachSoulCard calls enforce(\"ATTACH_AFFINITY\", gateCtx(), …) directly for the Affinity branch.",
    gateHereticalTraining:
      "R67/R68 — enforced. Shatterfield's handleHereticalTraining calls enforce(\"HERETICAL_TRAINING\", gateCtx(), …) directly.",
    gatePrimera:
      "R78-R84 — enforced. Shatterfield's stamp handler calls enforce(\"PRIMERA\", gateCtx(), …) directly with the word, the axis and both per-Turn ledgers.",
    gateAnimaGain:
      "R32 — enforced, by the OTHER shape in this file. `sealAnimaGain` is what the 32 gain sites adopted because it returns THE NUMBER TO WRITE, which is what their Math.min(MAX_ANIMA, …) returned; it calls enforce(\"GAIN_ANIMA\") itself and counts the ask in __animaSeal. This veto-shaped twin is the one nobody wanted.",
    phaseGate:
      "R108 — enforced, synchronously and elsewhere. Shatterfield's phaseBars asks rules.phaseAllows(actionType, phaseOf(liveMe)) directly, because a radial wedge's `legal` flag is computed during render and cannot await a gate. The enforcer also asks the same predicate ahead of every switch case in validateAction, so the phase is bound twice and this third door is unused.",
    gateCurioActivation:
      "R75/R108 — ⚠ NOTHING ACTIVATES A CURIO AT ALL, so there is no action to gate. A seat's Curios grant their gift ONCE, automatically, at the opening of the duel: Shatterfield's activateCuriosForPlayer for a mounted screen and NPC.activateNpcCurios for a duel without one, both guarded on the same `curiosActivated` flag. (A third copy of that table lived in a Shatterfield handler with zero call sites and was deleted on 2026-09-14; its tombstone is in that file.) RULEZ_V2 ch.14 says a Curio DOES activate and DOES pay a fare and carries OPT/OP2/OP3/ATV, so the book and the board disagree and the BOARD is the evidence — the catalogue row is V'ael's to move. This wrapper is the door the day a Curio can be pressed, and gateReach.selftest fails if that day arrives without its fare and its cadence being asserted.",
  },
};

// ════════════════════════════════════════════════════════════════════
// 👁 R119 · THE WARDEN'S TWO NULLIFIES ARE ARMED AND UNREAD
// ════════════════════════════════════════════════════════════════════
// Stated here because this file is where a reader looks for "what is
// enforced", and the honest answer for these two is "nothing yet".
//
// RULEZ_V2 ch.11 gives the Mind two answers, each once per two Turns,
// each 5 Khao or 5 Anima, each sending a card to the Necropolis on top
// of the fare: hand → nullify 1 card effect, Grimoire → nullify 1 Lyt
// Attack. `roleArts.performWardenArt` charges the fare, takes the card,
// and writes `wardenNullifyEffect` / `wardenNullifySwing` onto the seat
// stamped with the half-turn.
//
// ⚠ NOTHING READS EITHER FLAG. Not this file, not RulesEnforcer, not
// ResponseBridge, not tomeResolver, not the combat path. A player pays
// 5 and a card and gets a toast. Consuming them needs the response
// window and the combat path to ask, which is a build and not a
// coherency fix — so the GAP IS ASSERTED instead, in
// tools/gateReach.selftest.mjs §4, and the assertion fails the day a
// consumer appears. Then somebody deletes it on purpose and asserts the
// consumption. That is the only way a gap this shape does not rot into
// a bug nobody remembers choosing.

export default {
  enforce, setEffectiveLyfResolver, GATE_REACH,
  gateRoleArt, gateExiliation, gateExileRetrieval,
  gateSoulActivation, gateCurioActivation, gateDeclarePhase, phaseGate,
  gateDraw, gateDiscard, gateKhaoSpend, gatePrimera,
  gateAttackEffect, gateSearch, gateEffectSearch, gateMulligan,
  gateAnimaGain, sealAnimaGain, __animaSeal, resetAnimaSealStats,
};
